import { useState, useEffect, useCallback, useRef } from 'react';
import { s3Service } from '../services/s3Service';
import { AppState } from '../types';

/**
 * Custom hook for managing state synchronized with S3
 * 
 * Features:
 * - Fetches initial state from S3 on mount
 * - Implements optimistic updates (updates UI immediately, then syncs to S3)
 * - Returns [value, setValue, isLoading, error] tuple
 * - Handles S3 failures gracefully with fallback to in-memory state
 * 
 * @param key - The key in AppState to manage (e.g., 'challenges', 'powerBreak')
 * @param initialValue - Default value to use if S3 is unavailable
 * @returns Tuple of [value, setValue, isLoading, error]
 */
export function useS3State<K extends keyof AppState>(
  key: K,
  initialValue: AppState[K]
): [AppState[K], (value: AppState[K]) => Promise<void>, boolean, Error | null] {
  const [value, setValue] = useState<AppState[K]>(initialValue);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [s3Available, setS3Available] = useState(true);
  const isMountedRef = useRef(true);

  // Fetch initial state from S3 on mount
  useEffect(() => {
    let mounted = true;

    const fetchInitialState = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check if S3 is available
        const available = await s3Service.isAvailable();
        if (mounted) {
          setS3Available(available);
        }

        if (!available) {
          console.warn('S3 is not available, using in-memory state only');
          if (mounted) {
            setIsLoading(false);
          }
          return;
        }

        // Fetch state from S3
        const state = await s3Service.getState();
        
        if (mounted) {
          setValue(state[key]);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch initial state from S3:', err);
        
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to fetch state'));
          setS3Available(false);
          setIsLoading(false);
        }
      }
    };

    fetchInitialState();

    return () => {
      mounted = false;
      isMountedRef.current = false;
    };
  }, [key]);

  // Update value with optimistic updates
  const updateValue = useCallback(
    async (newValue: AppState[K]) => {
      // Optimistic update: update UI immediately
      setValue(newValue);

      // If S3 is not available, just update in-memory state
      if (!s3Available) {
        console.warn('S3 not available, state change will not persist');
        return;
      }

      // Sync to S3 in the background
      try {
        const updates: Partial<AppState> = {
          [key]: newValue,
        } as Partial<AppState>;

        await s3Service.updateState(updates);
        
        // Clear any previous errors on successful update
        if (isMountedRef.current) {
          setError(null);
        }
      } catch (err) {
        console.error('Failed to sync state to S3:', err);
        
        if (isMountedRef.current) {
          setError(err instanceof Error ? err : new Error('Failed to sync state'));
          
          // Mark S3 as unavailable if we get access errors
          if (err instanceof Error && err.message.includes('access denied')) {
            setS3Available(false);
          }
        }
        
        // Note: We don't revert the optimistic update
        // The UI shows the new state even if S3 sync fails
        // This provides better UX and the state will be in-memory only
      }
    },
    [key, s3Available]
  );

  return [value, updateValue, isLoading, error];
}
